import { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { StyledTableRow, StyledTableCell } from '@/components/ui/styled-table';
import { MemberChangeBadge } from '@/components/members/member-change-badge';
import { ApprovalCountsDisplay } from '@/components/shared/approval-counts-display';
import { VoteStatusBadge } from '@/components/shared/vote-status-badge';
import { type PendingProposalWithSignatures } from '@/hooks/admin/member/use-pending-member-proposals';
import { useAuthClient } from '@/lib/authClient';
import { getSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';
import { getEmailDisplay } from '@/types/user';
import { formatName } from '@/lib/name';

interface MemberProposalRowProps {
  proposal: PendingProposalWithSignatures;
  isSelected: boolean;
  onSelectChange: (changeId: string, checked: boolean) => void;
}

export function MemberProposalRow({
  proposal,
  isSelected,
  onSelectChange,
}: MemberProposalRowProps) {
  const { user } = useAuthClient();
  const member = proposal.targetUser;
  const changeBadges =
    proposal.proposal.type === 'ADD'
      ? [{ changeType: 'add', text: 'Add new member' } as const]
      : [
          {
            changeType: 'remove',
            text: 'Remove existing member',
          } as const,
        ];

  const { numApprovals, numRejections, userVote } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposal.approvals,
        rejections: proposal.rejections,
        userId: user?.id,
        requiredNetworkIds: proposal.requiredNetworkIds,
      }),
    [
      proposal.approvals,
      proposal.rejections,
      proposal.requiredNetworkIds,
      user?.id,
    ]
  );

  return (
    <StyledTableRow
      className={`cursor-pointer transition-colors ${
        isSelected ? 'selected' : ''
      }`}
      onClick={() => onSelectChange(proposal.proposal.id, !isSelected)}
    >
      <StyledTableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) =>
            onSelectChange(proposal.proposal.id, !!checked)
          }
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select change ${proposal.proposal.id}`}
        />
      </StyledTableCell>
      <StyledTableCell>
        <div className="flex flex-wrap gap-1.5">
          {changeBadges.map((badge, index) => (
            <MemberChangeBadge
              key={index}
              changeType={badge.changeType}
              text={badge.text}
            />
          ))}
        </div>
      </StyledTableCell>
      <StyledTableCell className="font-semibold text-base">
        {member?.name ? formatName(member.name) : 'N/A'}
      </StyledTableCell>
      <StyledTableCell>
        {member ? getEmailDisplay(member.email) : 'N/A'}
      </StyledTableCell>
      <StyledTableCell>
        <ApprovalCountsDisplay
          approvals={numApprovals}
          rejections={numRejections}
          threshold={proposal.requiredApprovals}
        />
      </StyledTableCell>
      <StyledTableCell>
        <VoteStatusBadge userVote={userVote} />
      </StyledTableCell>
    </StyledTableRow>
  );
}
